/**
 * sim/api — 디바이스 시뮬앱 전용 백엔드 호출 (web-admin medApi 의 sim·devices 서브셋).
 *
 * 공유 axios 인스턴스(@/api/client)를 사용 — Bearer 자동 부착 + 401 refresh 인터셉터 내장.
 * 응답 표준 포맷 {success, data} 언랩.
 */
import { api as inst } from '@/api/client'

function unwrap<T>(p: Promise<{ data: { success: boolean; data: T; error?: any } }>): Promise<T> {
  return p.then((r) => {
    if (r.data?.success === false) throw new Error(r.data?.error?.message ?? 'API_ERROR')
    return r.data.data
  })
}

export const medApi = {
  // 디바이스 목록 (시뮬 가능 디바이스 선택용)
  devices: {
    list: () => unwrap<any[]>(inst.get('/api/v1/pocket-device')),
    // 약달력 등록(테스트) — device 자동 생성 + 환자 + 바인딩(원자). GUARDIAN 가능, sim_allowed=1.
    pairBootstrap: (body: { device_code: string; patient: { name: string; birth_date?: string | null }; relation?: string }) =>
      unwrap<any>(inst.post('/api/v1/pocket-device/pair-bootstrap', body)),
  },
  // 3D 시뮬 — DeviceDetailModal 이 사용
  sim: {
    // weekOffset: 0=이번주 / -1=전주 / +1=다음주 — 백엔드가 해당 주 복용일정 28포켓 계산
    state: (code: string, weekOffset = 0) =>
      unwrap<any>(inst.get(`/api/v1/sim/device/${encodeURIComponent(code)}/state?week_offset=${weekOffset}`)),
    toggle: (code: string, idx: number, body: any) =>
      unwrap(inst.post(`/api/v1/sim/device/${encodeURIComponent(code)}/pocket/${idx}/toggle`, body)),
    refill: (code: string, body: any) =>
      unwrap(inst.post(`/api/v1/sim/device/${encodeURIComponent(code)}/refill`, body)),
    reset: (code: string) => unwrap(inst.post(`/api/v1/sim/device/${encodeURIComponent(code)}/reset`)),
    cronMissed: () => unwrap<{ inserted: number }>(inst.post('/api/v1/sim/cron/missed')),
  },
}

/** 로그인 — 포털과 동일 엔드포인트(JWT). 토큰은 localStorage(fw.*) 에 저장. */
export async function login(loginId: string, password: string): Promise<void> {
  const { data } = await inst.post('/api/v1/auth/login', { loginId, password })
  if (!data?.success) throw new Error(data?.error?.message ?? '로그인 실패')
  localStorage.setItem('fw.accessToken', data.data.accessToken)
  localStorage.setItem('fw.refreshToken', data.data.refreshToken)
}

/** 시뮬 세션 핸드오프 — 메인앱이 발급한 1회용 티켓을 동일 사용자 토큰으로 교환. */
export async function simExchange(ticket: string): Promise<void> {
  const { data } = await inst.post('/api/v1/auth/sim-exchange', { ticket })
  if (!data?.success) throw new Error(data?.error?.message ?? '티켓 교환 실패')
  localStorage.setItem('fw.accessToken', data.data.accessToken)
  localStorage.setItem('fw.refreshToken', data.data.refreshToken)
}

export function logout(): void {
  localStorage.removeItem('fw.accessToken')
  localStorage.removeItem('fw.refreshToken')
}

export function isLoggedIn(): boolean {
  return !!localStorage.getItem('fw.accessToken')
}

/** axios 에러를 사용자 메시지로 변환. */
export function toErrMsg(e: any): string {
  const body = e?.response?.data
  if (body?.error?.message) return body.error.message as string
  if (body?.detail?.message) return body.detail.message as string
  if (typeof body?.detail === 'string') return body.detail
  return e?.message ?? 'API_ERROR'
}
